import React, { useState, useMemo, useEffect } from "react";
import "./style.scss";
import { NavLink, useNavigate } from "react-router-dom";
import Icon from "../../common/ui/Icon";
import { TIconsNames } from "../../common/iconSets";
import TextTitle from "../../common/ui/typography/TextTitle";
import TextCaption from "../../common/ui/typography/TextCaption";
import { useGlobalStore } from "../../../store";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import TextBody from "../../common/ui/typography/TextBody";
import Button from "../../common/ui/buttons/Button";
import { useQuery } from "react-query";
import { statistics } from "../../../api/statistics";
import SetTruckLimitModal from "../../overview/components/TruckLimitModal/SetTruckLimitModal";
import moment from "moment";
import DueDateModal from "../../overview/components/DueDateModal/DueDateModal";

import { Platforms } from "../../../api/sidebar";

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const buildLogoUrl = (logoUrl?: string) => {
  if (!logoUrl) return "";
  if (logoUrl.startsWith("http")) return logoUrl;

  return `${process.env.REACT_APP_BASEURL}${logoUrl}`;
};

const Sidebar: React.FC<SidebarProps> = ({ sidebarOpen, setSidebarOpen }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDueDateModalOpen, setIsDueDateModalOpen] = useState(false);
  const navigate = useNavigate();

  const sidebarMobileOpen = useGlobalStore((state) => state.sidebarMobileOpen);
  const selectedCompany = useGlobalStore((state) => state.selectedCompany);
  const setSelectedCompany = useGlobalStore(
    (state) => state.setSelectedCompany,
  );
  const toggleMobileSidebar = useGlobalStore(
    (state) => state.toggleMobileSidebar,
  );

  const {
    data: truckLimit,
    isLoading,
    refetch,
  } = useQuery(["trucklimit", selectedCompany], () =>
    statistics.getTruckLimit(selectedCompany),
  );

  const { data: companiesList } = useQuery(
    ["platforms-dropdown"],
    Platforms.getPlatformsDropdown,
    { staleTime: 5 * 60 * 1000 },
  );

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const sidebarItems = useMemo<
    {
      link: string;
      title: string;
      icon: { active: TIconsNames; inActive: TIconsNames };
      show: boolean;
    }[]
  >(
    () => [
      {
        link: "/",
        title: "Overview",
        icon: { active: "view-desktop-filled", inActive: "view-desktop" },
        show: true,
      },
      {
        link: "/feedbacks",
        title: "Feedbacks",
        icon: { active: "comment-filled", inActive: "comment" },
        show: selectedCompany !== "all",
      },
      {
        link: "/heatmap",
        title: "Heatmap",
        icon: { active: "data-scatter-filled", inActive: "data-scatter" },
        show: true,
      },
      {
        link: "/force-update",
        title: "Force update",
        icon: { active: "history-filled", inActive: "history" },
        show: true,
      },
      {
        link: "/referral",
        title: "Referral",
        icon: { active: "referral-filled", inActive: "referral" },
        show: true,
      },
    ],
    [selectedCompany],
  );

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1280);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1280);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && sidebarMobileOpen) document.body.classList.add("no-scroll");
    else document.body.classList.remove("no-scroll");

    return () => document.body.classList.remove("no-scroll");
  }, [isMobile, sidebarMobileOpen]);

  const sidebarOpenCheck = isMobile ? sidebarMobileOpen : sidebarOpen;

  const closeSidebarOnMobile = () => {
    if (isMobile) toggleMobileSidebar();
  };

  const dropdownElements = useMemo(() => {
    return (companiesList ?? []).map((c: any) => ({
      title: c.title,
      value: c.value,
      logoUrl: buildLogoUrl(c.logoUrl),
      isWhiteLabel: c.isWhiteLabel,
    }));
  }, [companiesList]);

  const selectedCompanyItem = useMemo(() => {
    return dropdownElements.find((x) => x.value === selectedCompany);
  }, [dropdownElements, selectedCompany]);

  const onLogout = () => {
    localStorage.clear();
    navigate("auth/login");
  };

  const toggle = () => setDropdownOpen((prevState) => !prevState);

  const getProgressColor = (value: number, max: number): string => {
    if (value > max) return "red";
    if (value > max * 0.9) return "orange";
    return "blue";
  };

  return (
    <>
      {truckLimit && (
        <SetTruckLimitModal
          isOpen={isModalOpen}
          toggle={() => setIsModalOpen(!isModalOpen)}
          initialLimit={400}
          onSave={() => refetch()}
          truckLimit={truckLimit.quantity}
        />
      )}

      {truckLimit && isDueDateModalOpen && (
        <DueDateModal
          initialLimit={400}
          isOpen={isDueDateModalOpen}
          toggle={() => setIsDueDateModalOpen(!isDueDateModalOpen)}
          dueDate={truckLimit.paymentDueDay || 0}
          onSave={() => refetch()}
        />
      )}

      {sidebarOpenCheck && (
        <div className="sidebar-overlay" onClick={closeSidebarOnMobile} />
      )}

      <div className={`sidebar ${sidebarOpenCheck ? "open" : "closed"}`}>
        <div className="sidebarHeader">
          <Dropdown
            isOpen={dropdownOpen}
            toggle={toggle}
            className="sidebar-dropdown"
          >
            <DropdownToggle className="sidebar-dropdown__toggle" caret>
              {sidebarOpenCheck ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    width: "100%",
                  }}
                >
                  {selectedCompany === "all" ? (
                    <>
                      <Icon icon="grid-filled" />
                      <TextTitle className="color__black" level="3">
                        All Companies
                      </TextTitle>
                    </>
                  ) : (
                    <>
                      {selectedCompanyItem?.logoUrl ? (
                        <img
                          src={selectedCompanyItem.logoUrl}
                          alt={selectedCompanyItem.title}
                          className="sidebar-dropdown__logo"
                          style={{ width: 24, height: 24 }}
                        />
                      ) : (
                        <Icon icon="grid-filled" />
                      )}

                      <TextTitle className="color__black" level="3">
                        {selectedCompanyItem?.title || ""}
                      </TextTitle>
                    </>
                  )}

                  <div
                    style={{
                      cursor: "pointer",
                      marginLeft: "auto",
                      transform: dropdownOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.1s ease-in-out",
                    }}
                  >
                    <Icon
                      icon="chevron-down-blue"
                      className="sidebar-dropdown__icon"
                    />
                  </div>
                </div>
              ) : (
                <Icon
                  icon="chevron-down-blue"
                  className="sidebar-dropdown__icon"
                />
              )}
            </DropdownToggle>

            <DropdownMenu className="sidebar-dropdown__menu">
              {dropdownElements.map((el) => (
                <DropdownItem
                  key={el.value}
                  className="sidebar-dropdown__menu-item"
                  onClick={() => setSelectedCompany(el.value)}
                  active={el.value === selectedCompany}
                >
                  {el.logoUrl ? (
                    <img
                      src={el.logoUrl}
                      alt={el.title}
                      className="sidebar-dropdown__menu-icon"
                      style={{ width: 20, height: 20 }}
                    />
                  ) : (
                    <Icon
                      icon="grid-filled"
                      className="sidebar-dropdown__menu-icon"
                      size={"20px"}
                    />
                  )}
                  <TextBody level="1" className="sidebar-dropdown__menu-text">
                    {el.title}
                  </TextBody>
                  {el.value === selectedCompany && (
                    <Icon
                      icon="checkmark-circle-filled-blue"
                      className="sidebar-dropdown__menu-item-icon"
                    />
                  )}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>

        {sidebarOpenCheck && (
          <div className="sidebar__profile">
            <Icon icon="profile" />
            <div className="sidebar__profile--title">
              <TextTitle level="4">TT ELD</TextTitle>
              <TextCaption
                level="1"
                className="sidebar__profile--title-caption"
              >
                {user.first_name || ""} {user.second_name || ""}
              </TextCaption>
            </div>
          </div>
        )}

        {sidebarItems.map(
          (item) =>
            item.show && (
              <div className="sidebarMenu" key={item.link}>
                <div className="sidebarSubMenu">
                  <NavLink
                    className={({ isActive }) =>
                      isActive
                        ? "sidebarSubMenuHeadActive"
                        : "sidebarSubMenuHead"
                    }
                    to={item.link}
                    onClick={closeSidebarOnMobile}
                    style={
                      !sidebarOpenCheck
                        ? { justifyContent: "center", width: "44px" }
                        : undefined
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon
                          icon={
                            isActive ? item.icon.active : item.icon.inActive
                          }
                        />
                        {sidebarOpenCheck && item.title}
                      </>
                    )}
                  </NavLink>
                </div>
              </div>
            ),
        )}

        <div className="sidebarMenu" style={{ marginTop: "auto" }}>
          {sidebarOpenCheck &&
            !isMobile &&
            !isLoading &&
            selectedCompanyItem?.isWhiteLabel && (
              <>
                <div
                  className="truck-limit"
                  onClick={() => setIsDueDateModalOpen(true)}
                >
                  <div className="d-flex w-100 justify-content-between">
                    <div className="body-2">Payment due date</div>
                    <div className="body-2">
                      {truckLimit.paymentDueDay &&
                        moment().date(truckLimit.paymentDueDay).format("Do")}
                    </div>
                  </div>

                  {truckLimit.paymentDueDay &&
                    (() => {
                      const today = moment().startOf("day");
                      const dueDate = moment()
                        .date(truckLimit.paymentDueDay)
                        .startOf("day");
                      const passedDays = today.diff(dueDate, "days");

                      if (passedDays > 0 && passedDays <= 3) {
                        return (
                          <div className="caption-1">
                            {passedDays} {passedDays === 1 ? "day" : "days"}{" "}
                            passed
                          </div>
                        );
                      }

                      const nextDueDate =
                        passedDays > 3
                          ? moment()
                              .add(1, "month")
                              .date(truckLimit.paymentDueDay)
                              .startOf("day")
                          : dueDate;

                      const daysLeft = nextDueDate.diff(today, "days");

                      return (
                        <div className="caption-1">
                          {daysLeft} {daysLeft === 1 ? "day" : "days"} left
                        </div>
                      );
                    })()}
                </div>

                <div className="truck-limit mt-2">
                  <div className="truck-limit--header">
                    <TextBody level="1">Truck Limit</TextBody>
                    <TextBody level="1" className="d-flex">
                      {truckLimit.activeVehicleTotal}/
                      <TextTitle level="4">{truckLimit.quantity}</TextTitle>
                    </TextBody>
                  </div>

                  <div className="truck-limit--progress">
                    <div className="progress-bar-wrapper">
                      <div
                        className={`progress-bar ${getProgressColor(
                          truckLimit.totalTrucks,
                          truckLimit.quantity,
                        )}`}
                        style={{
                          width: `${Math.min(
                            (truckLimit.totalTrucks / truckLimit.quantity) *
                              100,
                            100,
                          )}%`,
                        }}
                      />
                    </div>
                  </div>

                  <Button
                    className="truck-limit--button"
                    color={
                      truckLimit.totalTrucks > truckLimit.quantity
                        ? "red"
                        : undefined
                    }
                    onClick={() => setIsModalOpen(true)}
                  >
                    Change limit
                  </Button>
                </div>
              </>
            )}
        </div>

        <div
          className="sidebarMenu"
          style={{ marginBottom: "20px" }}
          onClick={onLogout}
        >
          <div className="sidebarSubMenu">
            <div className="sidebarSubMenuHead">
              <Icon icon="logout" />
              {sidebarOpenCheck && "Logout"}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
